import React, { useState, useEffect } from "react";
import "./WorldInbox.css"; // Optional: Eigene Styles für diese Komponente
import { auth, database } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { ref, onValue, set } from "firebase/database";
import WorldInboxEmail from "./WorldInboxEmail";

// Füge den Loading-Spinner am Anfang der Komponente hinzu
const LoadingSpinner = () => (
  <div className="simple-loading-overlay">
    <div className="simple-loading-spinner"></div>
    <img src="/img/BonusClub.png" alt="BonusClub Logo" className="simple-loading-logo" />
    <p></p>
  </div>
);

const WorldInbox = () => {
  const [emails, setEmails] = useState([]);
  const [displayedEmails, setDisplayedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(1);
  const emailsPerPage = 10;
  const [hasMoreToFetch, setHasMoreToFetch] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [fetchingProgress, setFetchingProgress] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null); // ID des ausgewählten Kontos

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        console.log("Angemeldeter Benutzer:", user.email, "UID:", user.uid);
      } else {
        setUserId(null);
        console.log("Kein Benutzer angemeldet");
      }
    });
    return () => unsubscribe();
  }, []);

  // Lade die E-Mail-Konten des Benutzers
  useEffect(() => {
    if (!userId) return;

    const emailAccountsRef = ref(database, `/World/${userId}/settings/senderEmails`);
    const unsubscribe = onValue(emailAccountsRef, (snapshot) => {
      const accounts = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          accounts.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
      }
      
      // Wenn keine Konten gefunden wurden, prüfe die alten E-Mail-Einstellungen
      if (accounts.length === 0) {
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        onValue(emailSettingsRef, (settingsSnapshot) => {
          if (settingsSnapshot.exists()) {
            const settings = settingsSnapshot.val();
            if (settings.email_username) {
              accounts.push({
                id: 'default',
                email: settings.email_username,
                password: settings.email_password,
                imap_host: settings.imap_host || 'imap.hostinger.com',
                imap_port: settings.imap_port || '993',
                encryption_type: settings.encryption_type || 'SSL',
                smtp_host: settings.smtp_host || 'smtp.hostinger.com',
                smtp_port: settings.smtp_port || '465',
                smtp_encryption: settings.smtp_encryption || 'true'
              });
            }
          }
          setEmailAccounts(accounts);
          // Wähle das erste Konto als Standard
          if (accounts.length > 0 && !selectedAccount) {
            setSelectedAccount(accounts[0].id);
          }
        }, { onlyOnce: true });
      } else {
        setEmailAccounts(accounts);
        // Wähle das erste Konto als Standard
        if (accounts.length > 0 && !selectedAccount) {
          setSelectedAccount(accounts[0].id);
        }
      }
    });

    return () => unsubscribe();
  }, [userId, selectedAccount]);

  // Wenn ein Konto ausgewählt wird, lade die E-Mails für dieses Konto
  useEffect(() => {
    if (!userId || !selectedAccount) {
      return;
    }
    
    fetchInitialEmails();
  }, [userId, selectedAccount]);

  // Zähle ungelesene E-Mails
  useEffect(() => {
    const count = emails.filter(email => !email.isRead).length;
    setUnreadCount(count);
    console.log(`Ungelesene E-Mails: ${count} von ${emails.length}`);
  }, [emails]);

  const fetchInitialEmails = async () => {
    setLoading(true);
    setEmails([]);
    setPage(1);
    setHasMoreToFetch(true);
    
    try {
      // Hole E-Mails nur für das ausgewählte Konto
      const selectedAccountObj = emailAccounts.find(acc => acc.id === selectedAccount);
      if (selectedAccountObj) {
        // Temporär die E-Mail-Einstellungen in der Datenbank aktualisieren
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        await set(emailSettingsRef, {
          email_username: selectedAccountObj.email,
          email_password: selectedAccountObj.password,
          imap_host: selectedAccountObj.imap_host,
          imap_port: selectedAccountObj.imap_port,
          encryption_type: selectedAccountObj.encryption_type,
          smtp_host: selectedAccountObj.smtp_host,
          smtp_port: selectedAccountObj.smtp_port,
          smtp_encryption: selectedAccountObj.smtp_encryption
        });
        
        // Kurze Pause, um sicherzustellen, dass die Einstellungen aktualisiert wurden
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Rufe die E-Mails mit der bestehenden Cloud-Funktion ab
        const cloudFunctionURL = `https://us-central1-bonusclub-ab5e2.cloudfunctions.net/fetchInbox?uid=${userId}&page=${page}&sort=desc&timestamp=${Date.now()}`;
        
        const res = await fetch(cloudFunctionURL);
        
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.details || errorData.error || "Unbekannter Fehler");
        }
        
        const fetchedEmails = await res.json();
        
        // Füge Informationen über das Konto zu jeder E-Mail hinzu
        const emailsWithAccount = fetchedEmails.map(email => ({
          ...email,
          accountEmail: selectedAccountObj.email,
          accountId: selectedAccountObj.id
        }));
        
        setEmails(emailsWithAccount);
      }
    } catch (err) {
      console.error("Fehler beim Laden der E-Mails:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Aktualisiere die angezeigten E-Mails basierend auf der Seite
  useEffect(() => {
    if (emails.length === 0) {
      setDisplayedEmails([]);
      return;
    }
    
    const startIndex = 0;
    const endIndex = page * emailsPerPage;
    
    setDisplayedEmails(emails.slice(startIndex, endIndex));
  }, [emails, page, emailsPerPage]);

  const loadMoreEmails = async () => {
    setLoadingMore(true);
    
    try {
      // Wenn bereits alle geladenen E-Mails angezeigt werden, lade mehr
      if (displayedEmails.length >= emails.length && hasMoreToFetch) {
        const nextPage = page + 1;
        setPage(nextPage);
        
        // Hier müsste die Logik zum Laden weiterer E-Mails implementiert werden
        // Ähnlich wie in fetchInitialEmails, aber mit höherer Seitenzahl
        
        // Für jetzt simulieren wir, dass keine weiteren E-Mails gefunden wurden
        setHasMoreToFetch(false);
      } else {
        // Bestimme die Anzahl der zusätzlich anzuzeigenden E-Mails
        const additionalCount = emailsPerPage;
        
        // Berechne die neue Gesamtzahl der anzuzeigenden E-Mails
        const newDisplayCount = Math.min(
          displayedEmails.length + additionalCount,
          emails.length
        );
        
        // Aktualisiere die angezeigten E-Mails
        setDisplayedEmails(emails.slice(0, newDisplayCount));
      }
    } catch (error) {
      console.error("Fehler beim Laden weiterer E-Mails:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRefreshInbox = async (forceFresh = false) => {
    if (refreshing) return;
    
    setRefreshing(true);
    
    try {
      // Hole die neuesten E-Mails
      await fetchInitialEmails();
      
      console.log("Inbox erfolgreich aktualisiert");
    } catch (err) {
      console.error("Fehler beim Aktualisieren der Inbox:", err);
      setError("Aktualisierung fehlgeschlagen. Bitte versuchen Sie es später erneut.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteEmail = async (emailToDelete) => {
    try {
      const deleteURL = `https://us-central1-bonusclub-ab5e2.cloudfunctions.net/deleteEmail?uid=${userId}&messageUid=${emailToDelete.uid}`;
      const res = await fetch(deleteURL);
      if (!res.ok) {
        throw new Error("Fehler beim Löschen der E-Mail");
      }
      
      // Entferne die gelöschte E-Mail aus dem State
      const updatedEmails = emails.filter(email => email.uid !== emailToDelete.uid);
      setEmails(updatedEmails);
    } catch (err) {
      console.error("Error deleting email:", err);
    }
  };

  const handleMarkAsRead = async (emailToMark) => {
    try {
      console.log(`Markiere E-Mail ${emailToMark.uid} als gelesen...`);
      
      // API-Aufruf zum Markieren der E-Mail als gelesen
      const markAsReadURL = `https://us-central1-bonusclub-ab5e2.cloudfunctions.net/markEmailAsRead?uid=${userId}&messageUid=${emailToMark.uid}`;
      const res = await fetch(markAsReadURL);
      
      if (!res.ok) {
        throw new Error("Fehler beim Markieren der E-Mail als gelesen");
      }
      
      // Aktualisiere den Status der E-Mail im State
      setEmails(prevEmails => 
        prevEmails.map(email => 
          email.uid === emailToMark.uid 
            ? { ...email, isRead: true } 
            : email
        )
      );
      
      console.log(`E-Mail ${emailToMark.uid} erfolgreich als gelesen markiert`);
    } catch (err) {
      console.error("Fehler beim Markieren als gelesen:", err);
    }
  };

  // Berechne die Anzahl der verbleibenden E-Mails
  const remainingEmails = emails.length - displayedEmails.length;
  const hasMoreToShow = remainingEmails > 0 || hasMoreToFetch;

  const getEmailStatusSummary = () => {
    const totalEmails = emails.length;
    const readEmails = emails.filter(email => email.isRead).length;
    const unreadEmails = totalEmails - readEmails;
    
    return {
      total: totalEmails,
      read: readEmails,
      unread: unreadEmails
    };
  };

  return (
    <div className="worldInboxContainer">
      {loading && <LoadingSpinner />}
      
      <div className="worldInboxHeader">
        <h2>
          World Inbox
          {unreadCount > 0 && (
            <span className="unreadBadge">{unreadCount}</span>
          )}
        </h2>
        
        <div className="inboxControls">
          <div className="accountSelector">
            <select 
              value={selectedAccount || ''} 
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="modernDropdown"
              disabled={loading || refreshing}
            >
              {emailAccounts.length === 0 ? (
                <option value="">Keine E-Mail-Konten</option>
              ) : (
                emailAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.email}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>
      
      {loading ? (
        <p className="loadingText">
          <span className="loadingSpinner"></span>
        </p>
      ) : error ? (
        <p className="errorMessage">{error}</p>
      ) : (
        <>
          <div className="emailList">
            {displayedEmails.length === 0 ? (
              <p className="noEmailsMessage">Keine E-Mails gefunden.</p>
            ) : (
              displayedEmails.map((email, idx) => (
                <WorldInboxEmail 
                  key={`${email.accountId}-${email.uid}` || idx} 
                  email={email} 
                  userId={userId}
                  onDelete={handleDeleteEmail}
                  onMarkAsRead={handleMarkAsRead}
                />
              ))
            )}
          </div>
          
          {/* Debug-Info */}
          <div className="email-status-summary">
            <div className="email-status-counts">
              <span className="email-status-total">Gesamt: {getEmailStatusSummary().total}</span>
              <span className="email-status-divider">|</span>
              <span className="email-status-read">Gelesen: {getEmailStatusSummary().read}</span>
              <span className="email-status-divider">|</span>
              <span className="email-status-unread">Ungelesen: {getEmailStatusSummary().unread}</span>
            </div>
            <div className="email-display-info">
              Angezeigt: {displayedEmails.length} von {emails.length}
            </div>
          </div>
          
          {hasMoreToShow && (
            <div className="loadMoreButtonContainer">
              <button 
                className="loadMoreButton" 
                onClick={loadMoreEmails}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <span className="loadingSpinner"></span>
                    Lade...
                  </>
                ) : remainingEmails > 0 ? (
                  `Weitere ${Math.min(emailsPerPage, remainingEmails)} E-Mails anzeigen`
                ) : (
                  `Weitere E-Mails laden`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorldInbox; 